import React from 'react';
import { useTranslation } from 'localization';

export interface StarDisplayAtomProps {
  rating: number;
  maxStars?: number;
  size?: 'small' | 'normal' | 'large';
  showValue?: boolean;
  /**
   * Surface tone. `'light'` tints the empty/partial stars against a dark
   * background (used inside the scope-tinted featured hero). Default
   * `'dark'` keeps the original theme-token palette for light surfaces.
   */
  variant?: 'dark' | 'light';
  className?: string;
}

const StarDisplayAtom: React.FC<StarDisplayAtomProps> = ({
  rating,
  maxStars = 5,
  size = 'normal',
  showValue = false,
  variant = 'dark',
  className = '',
}) => {
  const { t } = useTranslation();

  const clampedRating = Math.min(Math.max(rating, 0), maxStars);

  const stars = Array.from({ length: maxStars }, (_, i) => {
    const starIndex = i + 1;
    const fill = clampedRating - i;

    let starClass = 'marketplace-star-display__star--empty';
    if (fill >= 1) {
      starClass = 'marketplace-star-display__star--filled';
    } else if (fill >= 0.25) {
      starClass = 'marketplace-star-display__star--partial';
    }

    return (
      <span
        key={starIndex}
        className={`marketplace-star-display__star ${starClass}`}
        aria-hidden="true"
      >
        {fill >= 0.25 ? '\u2605' : '\u2606'}
        {fill > 0 && fill < 1 && (
          <span
            className="marketplace-star-display__star-overlay"
            style={{ width: `${Math.round(fill * 100)}%` }}
          >
            {'\u2605'}
          </span>
        )}
      </span>
    );
  });

  const classes = [
    'marketplace-star-display',
    `marketplace-star-display--${size}`,
    `marketplace-star-display--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="img"
      aria-label={t('marketplace.ratings.average', { rating: clampedRating.toFixed(1) })}
    >
      <span className="marketplace-star-display__stars">{stars}</span>
      {showValue && (
        <span className="marketplace-star-display__value">
          {clampedRating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default StarDisplayAtom;
